import { z } from "zod";

/**
 * Subcontractor paperwork (spec §7): workers' comp (a policy, or the State of Oklahoma
 * exemption), general liability, and a W-9. Missing or expired paperwork is a soft stop:
 * work orders, bills, and expenses for that sub need a reason, which is logged.
 */

export type DocState = "ok" | "expiring" | "expired" | "missing" | "waived";

export interface ComplianceInput {
  wcKind: "policy" | "exemption" | null;
  wcFile: boolean;
  wcExpiresOn: string | null;
  glFile: boolean;
  glExpiresOn: string | null;
  w9File: boolean;
  /** No workers' comp needed (counts as done). */
  wcWaived?: boolean;
  /** No general liability needed (counts as done). */
  glWaived?: boolean;
  /** No W-9 needed. */
  w9Waived?: boolean;
  /** JobNimbus says the W-9 is on file (no document tagged here). */
  w9OnFile?: boolean;
}

export interface ComplianceStatus {
  workersComp: DocState;
  generalLiability: DocState;
  w9: DocState;
  /** Everything needed is on file and current (expiring soon still counts). */
  ok: boolean;
  /** Plain-language problems, for the soft stop and the contact page. */
  problems: string[];
}

/** Whether a dated document is current on `today` (YYYY-MM-DD), warning `warnDays` ahead. */
function dated(hasFile: boolean, expiresOn: string | null, today: string, warnDays: number): DocState {
  if (!hasFile || !expiresOn) return "missing";
  if (expiresOn < today) return "expired";
  const warn = new Date(`${today}T12:00:00Z`);
  warn.setUTCDate(warn.getUTCDate() + warnDays);
  return expiresOn <= warn.toISOString().slice(0, 10) ? "expiring" : "ok";
}

export function complianceStatus(c: ComplianceInput, today: string, warnDays = 30): ComplianceStatus {
  // The Oklahoma exemption still needs its certificate on file; it has an expiration too.
  const workersComp: DocState = c.wcWaived ? "waived" : c.wcKind ? dated(c.wcFile, c.wcExpiresOn, today, warnDays) : "missing";
  const generalLiability: DocState = c.glWaived ? "waived" : dated(c.glFile, c.glExpiresOn, today, warnDays);
  const w9: DocState = c.w9Waived ? "waived" : c.w9File || c.w9OnFile ? "ok" : "missing";
  const label = { workersComp: c.wcKind === "exemption" ? "Workers' comp exemption" : "Workers' comp", generalLiability: "General liability", w9: "W-9" };
  const problems: string[] = [];
  for (const [k, s] of Object.entries({ workersComp, generalLiability, w9 }) as [keyof typeof label, DocState][]) {
    if (s === "missing") problems.push(`${label[k]} isn't on file`);
    if (s === "expired") problems.push(`${label[k]} has expired`);
  }
  return { workersComp, generalLiability, w9, ok: problems.length === 0, problems };
}

// ── Sub insurance: the report, the dashboard card, and reminder emails ──

export const insuranceTemplateSchema = z.object({
  subject: z.string().trim().min(1).max(200),
  body: z.string().trim().min(1).max(5000),
});
export type InsuranceTemplate = z.infer<typeof insuranceTemplateSchema>;

/** Placeholders: {sub_name}, {policy}, {expires} (the date), {expires_phrase} ("expires on Nov 1, 2026" or "expired on Oct 1, 2026"), {company}, {company_phone}. The upload link is added as a button. */
export const DEFAULT_INSURANCE_TEMPLATE: InsuranceTemplate = {
  subject: "{company}: your {policy} certificate {expires_phrase}",
  body: "Hi {sub_name},\n\nOur records show your {policy} certificate {expires_phrase}. To keep working with us, please send your updated certificate of insurance. You can upload it with the button below; it only takes a minute.\n\nIf you have any questions, call us at {company_phone}.\n\nThank you,\n{company}",
};

/** The text version (the upload link is added at the end). */
export const DEFAULT_INSURANCE_TEXT = "Hi {sub_name}, this is {company}. Our records show your {policy} certificate {expires_phrase}. Please send us the updated certificate here:";

/** The Remind window, filled in from the templates. */
export interface InsuranceReminderDraft {
  email: { to: string; subject: string; message: string };
  text: { to: string; message: string };
  link: string;
  /** What's being chased, in words ("general liability expired on Oct 1, 2026"). */
  about: string;
  emailOn: boolean;
  textOn: boolean;
}

export const insuranceRemindSchema = z.object({
  how: z.enum(["email", "text"]),
  to: z.string().trim().min(3).max(254),
  subject: z.string().trim().max(200).default(""),
  message: z.string().trim().min(1).max(5000),
});

export interface InsuranceDoc {
  state: DocState;
  expiresOn: string | null;
}

/** A certificate a sub uploaded, read by the AI, waiting for the office to apply it. */
export interface CertificateCheckItem {
  id: string;
  status: "reading" | "ready" | "failed";
  fileId: string;
  fileName: string;
  fileUrl: string | null;
  uploadedAt: string;
  wcKind: "policy" | "exemption" | null;
  wcExpiresOn: string | null;
  glExpiresOn: string | null;
  note: string;
}

/** One sub on the insurance report and the dashboard card. */
export interface InsuranceRow {
  contactId: string;
  name: string;
  email: string;
  phone: string;
  inactive: boolean;
  waived: boolean;
  workersComp: InsuranceDoc & { kind: "policy" | "exemption" | null };
  generalLiability: InsuranceDoc;
  w9: DocState;
  /** Something to do: expired, expiring, missing, or a certificate to check. */
  attention: boolean;
  lastReminder: { at: string; policy: "wc" | "gl"; stage: "soon" | "expired" | "manual"; to: string; error: string | null } | null;
  check: CertificateCheckItem | null;
  lastWorkOrderAt: string | null;
}

export const certificateApplySchema = z.object({
  wcKind: z.enum(["policy", "exemption"]).nullable().default(null),
  wcExpiresOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().default(null),
  glExpiresOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().default(null),
});
